import { isFunction } from "../../utils/utils.js";
import { UiElement } from "../UiElement.js";

/**
 * Helper class for wrapping ThreeJS Camera, Renderer and Scene
 * 
 * @memberof vef.ui.webgl
 */
class ThreeJSContainer extends UiElement {

    /**
     * @param {HTMLElement | string} target 
     * @param {object} options 
     */
    constructor(target, options) {
        super(target, {
            "resize": [],
            "click": []
        });

        this.options = Object.assign({
            css2d: false,
            cameraPosition: new THREE.Vector3(0, 0, 0),
            fov: 45,
            near: 1,
            far: 1000,
            autostart: true
        }, options || {});

        this.renderers = {};
        this.renderCallbacks = [];
        this.camera = null;
        this.scene = new THREE.Scene();
        this.raycaster = new THREE.Raycaster();

        this.initElement_();
        this.initRenderers_();
        this.initCamera_();
        this.initClickEvent_();
        this.initAutoResize_();

        if (this.options.autostart) this.startRenderer();
    }

    initElement_() {
        const element = this.getElement();
        element.classList.add("vef-threejs-container");
        element.style.width = "100%";
        element.style.height = "100%";
    }

    initRenderers_() {
        const element = this.getElement();

        const init = (name, rendererClass) => {
            const renderer = new rendererClass();
            renderer.setSize(1, 1);

            const canvas = renderer.domElement;
            canvas.style.position = "absolute";
            canvas.style.inset = "0 0 0 0";

            this.renderers[name] = renderer;
            element.appendChild(renderer.domElement);
        }

        init("webgl", THREE.WebGLRenderer);
        if (this.options.css2d) init("css2d", THREE.CSS2DRenderer);
    }

    initCamera_() {
        const opts = this.options;
        this.camera = new THREE.PerspectiveCamera(opts.fov, 1, opts.near, opts.far);
        this.camera.position.copy(opts.cameraPosition);
        this.scene.add(this.camera);
    }

    resize_() {
        const element = this.getElement();
        const width = element.offsetWidth;
        const height = element.offsetHeight;

        if (Number.isFinite(width) && Number.isFinite(height)) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            for (let name in this.renderers) {
                this.renderers[name].setSize(width, height);
            }
        }

        this.fire("resize", { width: width, height: height });
    }

    initAutoResize_() {
        const resizeObserver_ = new ResizeObserver(() => this.resize_());
        resizeObserver_.observe(this.getElement());
    }

    initClickEvent_() {
        const clickTarget = this.getCanvas("webgl");
        let x = null;
        let y = null;

        clickTarget.addEventListener("pointerdown", e => {
            x = e.offsetX;
            y = e.offsetY;
        });

        clickTarget.addEventListener("pointerup", e => {
            if ((e.offsetX == x) && (e.offsetY == y)) {
                this.fire("click", {
                    absolutePoint: new THREE.Vector2(x, y),
                    normalizedPoint: new THREE.Vector2(
                        (x / clickTarget.offsetWidth) * 2 - 1,
                        -((y / clickTarget.offsetHeight) * 2 - 1)
                    )
                })
            }
            x = null;
            y = null;
        });
    }

    appendTo(target, position) {
        super.appendTo(target, position);
        setTimeout(() => this.resize_(), 0);
    }

    startRenderer() {
        const clock = new THREE.Clock(true);

        const animate = () => {
            requestAnimationFrame(animate);

            // update time delta for animations
            const delta = clock.getDelta();

            // run render callbacks and remove the callback if it returns true
            for (let i = 0; i < this.renderCallbacks.length; ++i) {
                const remove = this.renderCallbacks[i](delta);
                if (remove) this.renderCallbacks.splice(i--, 1);
            }

            for (let name in this.renderers) {
                this.renderers[name].render(this.scene, this.camera);
            }
        }

        animate();
        this.resize_();
    }

    addRenderCallback(fn) {
        if (isFunction(fn)) this.renderCallbacks.push(fn);
    }

    getCanvas(name) {
        if (name in this.renderers) return this.renderers[name].domElement;
    }

    raycast(point, objects) {
        this.raycaster.setFromCamera(point, this.camera);
        if (Array.isArray(objects)) {
            return this.raycaster.intersectObjects(objects);
        } else {
            return this.raycaster.intersectObject(objects);
        }
    }

}

export { ThreeJSContainer };